import React from 'react';
import { baseURL } from '../../api/baseURL';
import axios from 'axios';
import { connect } from 'react-redux';
import Loader from '../../Loader';
import Modal from '../../Modal';
import { MDBBtn, MDBInput } from 'mdb-react-ui-kit';
import { isMainAdmin } from '../../utils/isMainAdmin';
import AdminShell from '../layout/AdminShell';
import { countPillStyle, thStyle } from '../layout/listUi';

type AdminRow = { _id: string, name: string, email: string, role: string, createdAt: string };

// Client-reported: "the main admin can add max 2 admin (including main) and
// 4 sub admin max". These are also enforced server-side (the actual source
// of truth — Admin Server/src/apis/auth.js) — mirrored here just so the UI
// can show "limit reached" instead of a confusing generic error.
const MAIN_ADMIN_LIMIT = 2;
const SUB_ADMIN_LIMIT = 4;

type AdminFormKind = 'main' | 'sub';
type AdminFormState = {
    name: string, email: string, password: string,
    // See renderCreateForm's Email field: starts true so the field is
    // excluded from Chrome's autofill candidate scan entirely, flips false
    // the instant it's focused.
    emailFieldLocked: boolean,
};
const emptyAdminForm = (): AdminFormState => ({ name: '', email: '', password: '', emailFieldLocked: true });

type State = {
    requested: boolean,
    err: boolean,
    admins: AdminRow[],
    forms: Record<AdminFormKind, AdminFormState>,
    formErr: Record<AdminFormKind, string>,
    removeConfirmId: string,
};

/** Main Admin (edit) vs Sub Admin (view-only) — Phase 2 §11. This page is
 * itself main-admin-only (creating accounts is inherently an edit action);
 * the real enforcement is server-side (requireMainAdmin middleware), this is
 * just the UI to manage it. */
class AdminsPage extends React.Component<{ history: any, token: string, role?: string, email?: string }, State> {

    state: State = {
        requested: false,
        err: false,
        admins: [],
        forms: { main: emptyAdminForm(), sub: emptyAdminForm() },
        formErr: { main: '', sub: '' },
        removeConfirmId: '',
    }

    componentDidMount(): void {
        if (!this.props.token) {
            window.location.pathname = "/";
        }
        else {
            this.getList();
        }
    }

    getList = () => {
        this.setState({ requested: true, err: false });
        axios.get(`${baseURL}/admins`, { headers: { Authorization: `Bearer ${this.props.token}` } })
            .then((response: any) => {
                if (response?.data?.message === "success") {
                    this.setState({ admins: response.data.admins || [], requested: false });
                } else {
                    this.setState({ err: true, requested: false });
                }
            })
            .catch(() => this.setState({ err: true, requested: false }));
    }

    createAccount = (kind: AdminFormKind) => {
        const form = this.state.forms[kind];
        if (this.state.requested || !form.name || !form.email || !form.password) return;
        // type="text" (see renderCreateForm) drops the browser's native email
        // format check, and the server doesn't check format either (only
        // presence + uniqueness) — so this is the only format guard.
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) return;
        this.setState({ requested: true, err: false, formErr: { ...this.state.formErr, [kind]: '' } });
        const endpoint = kind === 'main' ? '/create-admin' : '/create-sub-admin';
        axios.post(`${baseURL}${endpoint}`,
            { name: form.name, email: form.email, password: form.password },
            { headers: { Authorization: `Bearer ${this.props.token}` } })
            .then((response: any) => {
                if (response?.data?.message === "success") {
                    this.setState({ requested: false, forms: { ...this.state.forms, [kind]: emptyAdminForm() } });
                    this.getList();
                } else {
                    this.setState({ requested: false, err: true });
                }
            })
            .catch((error: any) => {
                const msg = error?.response?.data?.message;
                const friendly =
                    msg === 'admin_limit_reached' ? `Maximum of ${MAIN_ADMIN_LIMIT} admins reached.` :
                    msg === 'sub_admin_limit_reached' ? `Maximum of ${SUB_ADMIN_LIMIT} sub admins reached.` :
                    msg === 'email_exists' ? 'That email is already registered.' :
                    '';
                if (friendly) {
                    this.setState({ requested: false, formErr: { ...this.state.formErr, [kind]: friendly } });
                } else {
                    this.setState({ requested: false, err: true });
                }
            });
    }

    toggleModal = () => {
        if (this.state.err) this.setState({ err: false });
    }

    removeAdmin = (adminId: string) => {
        if (this.state.requested) return;
        this.setState({ requested: true, err: false, removeConfirmId: '' });
        axios.post(`${baseURL}/remove-admin`,
            { adminId },
            { headers: { Authorization: `Bearer ${this.props.token}` } })
            .then((response: any) => {
                if (response?.data?.message === "success") {
                    this.getList();
                } else {
                    this.setState({ err: true, requested: false });
                }
            })
            .catch(() => this.setState({ err: true, requested: false }));
    }

    setForm = (kind: AdminFormKind, patch: Partial<AdminFormState>) => {
        this.setState({ forms: { ...this.state.forms, [kind]: { ...this.state.forms[kind], ...patch } } });
    }

    /** Shared shape for both "Add Admin" and "Add Sub Admin" — same fields,
     * same Chrome-autofill workarounds, only the title/button/limit differ. */
    renderCreateForm = (kind: AdminFormKind, title: string, buttonLabel: string, count: number, limit: number) => {
        const form = this.state.forms[kind];
        const limitReached = count >= limit;
        return (
            <div className="card" style={{ marginBottom: 16 }}>
                <div className="card-body">
                    <h4 className="card-title">{title}</h4>
                    {limitReached ? (
                        <p style={{ margin: 0, color: '#FFB020' }}>Maximum of {limit} reached — remove one below to add another.</p>
                    ) : (
                        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                            {/* Client-reported: the account-suggestion dropdown kept coming
                                back even after switching Email to type="text" and giving it a
                                non-email `name` — because Chrome doesn't only key off type/name,
                                it also pattern-matches "a text field sitting right before a
                                password field" as a login form, and reads the visible "Email"
                                label text itself. The standard workaround: two decoy fields
                                (off-screen, not display:none — Chrome ignores display:none ones)
                                ahead of the real fields, so its heuristics latch onto these
                                instead. Never focusable/visible/submitted. */}
                            <input type="text" name={`username-${kind}`} autoComplete="username" tabIndex={-1} aria-hidden="true" style={{ position: 'absolute', width: 1, height: 1, padding: 0, border: 0, opacity: 0, pointerEvents: 'none' }} />
                            <input type="password" name={`password-${kind}`} autoComplete="current-password" tabIndex={-1} aria-hidden="true" style={{ position: 'absolute', width: 1, height: 1, padding: 0, border: 0, opacity: 0, pointerEvents: 'none' }} />
                            <MDBInput contrast autoComplete="off" name={`admin-name-${kind}`} label="Name" type="text" value={form.name} onChange={(e) => this.setForm(kind, { name: e.target.value })} />
                            {/* type="text" (not "email") deliberately — see note above; also
                                drops the browser's native email-format check, which
                                createAccount above now does itself. */}
                            <MDBInput
                                contrast
                                autoComplete="off"
                                name={`admin-email-${kind}`}
                                label="Email"
                                type="text"
                                // "Manage Addresses..." in the dropdown confirmed this is
                                // Chrome's separate Address/Contact autofill, which reads the
                                // associated <label> text directly and ignores
                                // autocomplete="off" for it too. The one thing that reliably
                                // works: a field marked readOnly is excluded from Chrome's
                                // autofill candidate scan entirely, so it starts readOnly and
                                // flips editable the instant it's focused — invisible to the
                                // user, invisible to Chrome's scan.
                                readOnly={form.emailFieldLocked}
                                onFocus={() => { if (form.emailFieldLocked) this.setForm(kind, { emailFieldLocked: false }); }}
                                value={form.email}
                                onChange={(e) => this.setForm(kind, { email: e.target.value })}
                            />
                            <MDBInput contrast autoComplete="new-password" name={`admin-password-${kind}`} label="Password" type="password" value={form.password} onChange={(e) => this.setForm(kind, { password: e.target.value })} />
                            <MDBBtn onClick={() => this.createAccount(kind)} disabled={this.state.requested}>{buttonLabel}</MDBBtn>
                        </div>
                    )}
                    {this.state.formErr[kind] ? <p style={{ color: '#ff6b6b', fontSize: 13, marginTop: 10, marginBottom: 0 }}>{this.state.formErr[kind]}</p> : null}
                </div>
            </div>
        );
    }

    render() {
        const mainAdmin = isMainAdmin(this.props.role);
        // A missing/legacy role counts as 'main' — same convention the
        // server and the role badge below already use.
        const mainAdminCount = this.state.admins.filter(a => a.role !== 'sub').length;
        const subAdminCount = this.state.admins.filter(a => a.role === 'sub').length;
        return (
            <AdminShell activePage="admins" title="Admins & Roles" subtitle="Manage admin accounts and access levels">
                <Modal open={this.state.err} hideSec primaryText="Okay" title='Update' onConfirm={this.toggleModal} toggleOpen={this.toggleModal} content={<h6>Something went wrong, please try again later</h6>} />
                <Modal
                    open={!!this.state.removeConfirmId}
                    primaryText="Remove"
                    secondaryText="Cancel"
                    title='Remove admin'
                    onConfirm={() => this.removeAdmin(this.state.removeConfirmId)}
                    toggleOpen={() => this.setState({ removeConfirmId: '' })}
                    content={<h6>Remove this admin account? This cannot be undone.</h6>}
                />
                {this.state.requested ? <Loader /> : null}
                {!mainAdmin ? (
                    <div className="card" style={{ marginBottom: 16 }}>
                        <div className="card-body">
                            <p style={{ margin: 0, color: '#FFB020' }}>Sub Admins have view-only access and cannot manage admin accounts.</p>
                        </div>
                    </div>
                ) : (
                    <>
                        {this.renderCreateForm('main', 'Add Admin (full access)', 'Add Admin', mainAdminCount, MAIN_ADMIN_LIMIT)}
                        {this.renderCreateForm('sub', 'Add Sub Admin (view-only)', 'Add Sub Admin', subAdminCount, SUB_ADMIN_LIMIT)}
                    </>
                )}
                <div className="card">
                    <div className="card-body">
                        <h4 className="card-title">
                            Admin Accounts
                            {this.state.admins.length ? <span style={countPillStyle}>{this.state.admins.length}</span> : null}
                        </h4>
                        <div className="table-responsive">
                            {this.state.admins.length ? (
                                <table className="table">
                                    <thead>
                                        <tr><th style={thStyle}>Name</th><th style={thStyle}>Email</th><th style={thStyle}>Role</th><th style={thStyle}>Created</th>{mainAdmin ? <th style={thStyle}></th> : null}</tr>
                                    </thead>
                                    <tbody>
                                        {this.state.admins.map((a) => (
                                            <tr key={a._id}>
                                                <td>{a.name || '-'}</td>
                                                <td>{a.email}</td>
                                                <td>
                                                    <span className={`badge ${a.role === 'main' ? 'badge-outline-success' : 'badge-outline-secondary'}`}>
                                                        {a.role === 'main' ? 'Main Admin (edit)' : 'Sub Admin (view only)'}
                                                    </span>
                                                </td>
                                                <td>{a.createdAt ? new Date(a.createdAt).toLocaleDateString() : '-'}</td>
                                                {mainAdmin ? (
                                                    <td>
                                                        {a.email !== this.props.email ? (
                                                            <MDBBtn color='danger' size='sm' onClick={() => this.setState({ removeConfirmId: a._id })}>Remove</MDBBtn>
                                                        ) : null}
                                                    </td>
                                                ) : null}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            ) : <div style={{ display: 'flex', justifyContent: 'center', padding: 15, paddingTop: 30 }}>
                                <div className="badge badge-outline-secondary" style={{ fontSize: 16 }}>No Data</div>
                            </div>}
                        </div>
                    </div>
                </div>
            </AdminShell>
        );
    }
}

const mapStateToProps = (state: any) => ({
    token: state.auth.token,
    role: state.auth.role,
    email: state.auth.email,
});

export default connect(mapStateToProps)(AdminsPage);
